import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import { useHistory } from "react-router";
import { useSelector } from "src/redux/store";
import axios from "src/utils/axios";
import IsRole from "src/utils/IsRole";
import Roles from "src/utils/Roles";
import { PATH_APP } from "src/routes/paths";
import { localISODate } from "src/utils/localDate";
import { whatsAppQuotaAlert } from "src/utils/whatsappQuota";

// Evento de ventana que emite useNotificationRestauranteHub cuando el
// backend bloquea un WhatsApp por cuota agotada.
export const WHATSAPP_QUOTA_EXHAUSTED_EVENT = "tmo:whatsapp-quota-exhausted";

const DISMISS_KEY = "tmo_wa_quota_banner_dismissed";

function today() {
  return localISODate();
}

function isDismissedToday(level) {
  try {
    return window.localStorage.getItem(`${DISMISS_KEY}_${level}`) === today();
  } catch (_) {
    return false;
  }
}

// Aviso de cuota WhatsApp para el titular del restaurante:
//  - rojo: sin mensajes (agotados, o la suscripción no incluye ninguno) y
//    con avisos por WhatsApp configurados, o cuando el backend avisa de un
//    envío bloqueado.
//  - ámbar: queda < 10 % de lo del ciclo (plan + packs).
// Reglas en `src/utils/whatsappQuota.js`.
export default function WhatsAppQuotaBanner() {
  const history = useHistory();
  const { claims } = useSelector((state) => state.authJwt);
  const isOwner =
    IsRole(claims?.role, Roles.USER_NORMAL) ||
    IsRole(claims?.role, Roles.USER_ADMIN);

  const [usage, setUsage] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [, forceRender] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await axios.get("whatsapp/usage/current");
      setUsage(res.data);
    } catch (_) {
      // Sin datos no mostramos nada.
    }
  }, []);

  useEffect(() => {
    if (!isOwner) return undefined;
    load();
    const onExhausted = () => {
      setBlocked(true);
      load();
    };
    window.addEventListener(WHATSAPP_QUOTA_EXHAUSTED_EVENT, onExhausted);
    return () => window.removeEventListener(WHATSAPP_QUOTA_EXHAUSTED_EVENT, onExhausted);
  }, [isOwner, load]);

  if (!isOwner) return null;
  const alert = whatsAppQuotaAlert(usage, { blocked });
  const level = alert?.level;
  if (!alert || isDismissedToday(level)) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(`${DISMISS_KEY}_${level}`, today());
    } catch (_) {}
    forceRender((n) => n + 1);
  };

  const goToPacks = () => {
    history.push(`${PATH_APP.user.account}?tab=notifications&sub=usage`);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity={alert.severity}
        onClose={dismiss}
        action={
          <Button color="inherit" size="small" onClick={goToPacks}>
            Comprar pack
          </Button>
        }
      >
        {alert.message}
      </Alert>
    </Box>
  );
}
